import { useQuery } from "@tanstack/react-query";
import { compact, sortBy } from "lodash/fp";

import { getMetricMetaById, QueryMetric } from "@app/analytics/meetops/metrics";
import apiClient from "@app/common/api/apiClient";
import type { SessionsListQuery } from "@app/common/api/apiClient/sessions";
import useAuth from "@app/common/auth/useAuth";
import type {
  ApiSessionsListEntity,
  User,
  UserMeeting,
} from "@app/common/types";
import * as SessionUtils from "@app/common/utils/sessionUtils";
import Config from "@app/config";
import {
  optionalParseUTCDateTimeFromString,
  parseUTCDateTimeFromString,
} from "@app/utils/utils";

const rawToValue = (metricId: QueryMetric, rawValue: number | null) =>
  getMetricMetaById(metricId).toValue(rawValue);

const getParsedResults = (
  rawQueryResults: ApiSessionsListEntity[],
  userId: User["id"] | undefined
): UserMeeting[] => {
  return compact(rawQueryResults).map((rawMeeting: ApiSessionsListEntity) => {
    const metricsAreEmpty = !rawMeeting.metrics?.observedDuration;

    const isUploaded = SessionUtils.isUploadedSession(rawMeeting);

    const startTime = parseUTCDateTimeFromString(
      metricsAreEmpty || isUploaded
        ? rawMeeting.startTime
        : rawMeeting.metrics.observedStartTime
    );

    const endTime =
      metricsAreEmpty || isUploaded
        ? rawMeeting.endTime
          ? parseUTCDateTimeFromString(rawMeeting.endTime)
          : null
        : startTime.plus({
            seconds: rawMeeting.metrics.observedDuration,
          });

    const scheduledStartTime = optionalParseUTCDateTimeFromString(
      rawMeeting.metrics.scheduledStartTime
    );
    const scheduledEndTime =
      scheduledStartTime && rawMeeting.metrics.scheduledDuration
        ? scheduledStartTime.plus({
            seconds: rawMeeting.metrics.scheduledDuration,
          })
        : undefined;

    if (metricsAreEmpty) {
      const emptyMetricsMeeting: UserMeeting = {
        ...rawMeeting,
        title: rawMeeting.title ?? "Untitled Meeting",
        numParticipants: 0,
        startTime,
        endTime,
        scheduledStartTime,
        scheduledEndTime,
        hasConferenceRoom: false,
        ownerName: rawMeeting.ownerUserName,
        ownerIsCurrentUser: userId === rawMeeting.ownerUserId,
        meetingScore: null,
        sentiment: null,
        engagement: null,
        talkTimePercent: null,
        keywords: [],
        lateBy: null,
        isMissed: false,
        meetingPlatform: rawMeeting.meetingPlatform ?? "unknown",
        thumbnailUrl: null,
        state: rawMeeting.state ?? "ended",
        tags: sortBy("name", rawMeeting.tags),
        permissions: rawMeeting.permissions ?? [],
      };
      return emptyMetricsMeeting;
    }

    const meeting: UserMeeting = {
      ...rawMeeting,
      seriesId: rawMeeting.metrics.recurringEventId,
      title: rawMeeting.title ?? "Untitled Meeting",
      meetingScore: rawToValue("meetingScore", rawMeeting.metrics.meetingScore),
      sentiment: rawToValue("sentiment", rawMeeting.metrics.sentiment),
      engagement: rawToValue("engagement", rawMeeting.metrics.engagement),
      talkTimePercent: rawToValue(
        "talkTimePercent",
        rawMeeting.metrics.talkTimeRatio
      ),
      numParticipants:
        rawToValue(
          "observedParticipantCount",
          rawMeeting.metrics.observedParticipantCount
        ) ?? 0,
      startTime,
      endTime,
      scheduledStartTime,
      scheduledEndTime,
      keywords: rawMeeting.metrics.topics ?? [],
      lateBy: rawMeeting.metrics?.participantLateBy,
      isMissed: (rawMeeting.metrics?.participantMissedMeetings ?? 0) > 0,
      hasConferenceRoom: rawMeeting.metrics.hasConferenceRoom,
      ownerName: rawMeeting.ownerUserName,
      ownerIsCurrentUser: userId === rawMeeting.ownerUserId,
      tags: sortBy("name", rawMeeting.tags),
      permissions: rawMeeting.permissions ?? [],
    };

    return meeting;
  });
};

export const getSessions = async (
  query: SessionsListQuery,
  isForCurrentUser: boolean,
  userId: User["id"] | undefined
): Promise<UserMeeting[]> => {
  const response = await apiClient.sessions.listSessions(
    query,
    isForCurrentUser
  );

  return getParsedResults(response, userId);
};

const useSessionsListQuery = (
  query: SessionsListQuery,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchInterval?: number;
    includeWorkspaceId?: boolean;
  }
) => {
  const { user } = useAuth();

  const sessionsListQuery: SessionsListQuery | null = query;

  const isForCurrentUser =
    query?.ownerUserId != null && query.ownerUserId == user?.id;

  return useQuery({
    queryKey: ["sessionsList", sessionsListQuery],
    queryFn: () => getSessions(sessionsListQuery, isForCurrentUser, user?.id),
    ...options,
    enabled: !Config.api.disableMetricsQueries && (options?.enabled ?? true),
  });
};

export default useSessionsListQuery;
